import { z } from "zod";
import { PERMISSIONS } from "./permissions.js";

/**
 * CT-RM-19: OAuth 2.0 authorization server for third-party apps.
 *
 * Scopes ARE permissions: an OAuth scope is a name from the PERMISSIONS
 * catalog (e.g. `constituent:read`), exactly like an API token scope. A token's
 * effective access is always (scopes granted) ∩ (the authorizing user's role).
 * There is no "*" scope for OAuth — apps must ask for what they need.
 */

export const OAUTH_CLIENT_TYPES = ["confidential", "public"] as const;
export type OAuthClientType = (typeof OAUTH_CLIENT_TYPES)[number];

/** seconds */
export const OAUTH_ACCESS_TOKEN_TTL = 3600;
export const OAUTH_REFRESH_TOKEN_TTL = 30 * 86_400;
export const OAUTH_CODE_TTL = 300;

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

/**
 * Registered redirect URIs: https anywhere, or http on a loopback host (native
 * and local-dev clients, RFC 8252 §7.3). No fragments, no embedded credentials.
 * Matching at authorize time is exact string equality against this list.
 */
export function redirectUriProblem(uri: string): string | null {
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return "must be an absolute URL";
  }
  if (url.hash || uri.includes("#")) return "must not contain a fragment (#)";
  if (url.username || url.password) return "must not contain credentials";
  if (url.protocol === "https:") return null;
  if (url.protocol === "http:" && LOOPBACK_HOSTS.has(url.hostname)) return null;
  return "must use https (http is allowed only for localhost / 127.0.0.1)";
}

export const zRedirectUri = z
  .string()
  .trim()
  .max(500)
  .superRefine((v, ctx) => {
    const problem = redirectUriProblem(v);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Redirect URI ${problem}` });
  });

export const zOAuthScope = z.enum(PERMISSIONS);

const zLogoUrl = z
  .string()
  .trim()
  .url()
  .max(500)
  .refine((v) => v.startsWith("https://"), "Logo URL must use https");

export const zCreateOAuthApp = z.object({
  name: z.string().trim().min(2).max(100),
  logoUrl: zLogoUrl.nullable().optional(),
  redirectUris: z.array(zRedirectUri).min(1).max(10),
  clientType: z.enum(OAUTH_CLIENT_TYPES),
  scopes: z.array(zOAuthScope).min(1),
  /** requests per API rate-limit window, per authorizing org; omitted = platform default */
  rateLimit: z.number().int().min(1).max(100_000).nullable().optional(),
});
export type CreateOAuthApp = z.infer<typeof zCreateOAuthApp>;

/** client type is fixed at registration (switching would change how the secret works) */
export const zUpdateOAuthApp = z
  .object({
    name: z.string().trim().min(2).max(100).optional(),
    logoUrl: zLogoUrl.nullable().optional(),
    redirectUris: z.array(zRedirectUri).min(1).max(10).optional(),
    scopes: z.array(zOAuthScope).min(1).optional(),
    rateLimit: z.number().int().min(1).max(100_000).nullable().optional(),
  })
  .strict();
export type UpdateOAuthApp = z.infer<typeof zUpdateOAuthApp>;

export interface OAuthAppSummary {
  id: string;
  clientId: string;
  name: string;
  logoUrl: string | null;
  redirectUris: string[];
  clientType: OAuthClientType;
  scopes: string[];
  rateLimit: number | null;
  /** e.g. "ctsec_ab12…" for confidential clients; never the secret itself */
  secretHint: string | null;
  secretRotatedAt: string | null;
  /** organizations currently holding a live (unrevoked, unexpired) refresh token */
  connectedOrgs: number;
  createdAt: string;
}

export interface CreatedOAuthApp {
  app: OAuthAppSummary;
  /** confidential clients only; returned exactly once */
  clientSecret: string | null;
}

export interface RotatedOAuthSecret {
  clientId: string;
  /** returned exactly once */
  clientSecret: string;
}

/** the query string a client sends to /oauth/authorize (RFC 6749 §4.1.1 + RFC 7636) */
export const zOAuthAuthorizeRequest = z.object({
  response_type: z.string().optional(),
  client_id: z.string().max(200).optional(),
  redirect_uri: z.string().max(500).optional(),
  scope: z.string().max(4000).optional(),
  state: z.string().max(1000).optional(),
  code_challenge: z.string().max(200).optional(),
  code_challenge_method: z.string().max(20).optional(),
});
export type OAuthAuthorizeRequest = z.infer<typeof zOAuthAuthorizeRequest>;

/** consent decision posted by the consent screen */
export const zOAuthAuthorizeDecision = zOAuthAuthorizeRequest.extend({
  organizationId: z.string().uuid(),
  approve: z.boolean(),
});
export type OAuthAuthorizeDecision = z.infer<typeof zOAuthAuthorizeDecision>;

export interface OAuthConsentScope {
  scope: string;
  description: string;
  /** false when the signed-in user's role doesn't hold it in that org — it won't be granted */
  available: boolean;
}

export interface OAuthConsentOrg {
  id: string;
  name: string;
  /** availability of each requested scope differs by org (roles are per org) */
  scopes: OAuthConsentScope[];
}

export interface OAuthConsentPreview {
  app: { name: string; logoUrl: string | null; clientId: string; publisher: string };
  redirectUri: string;
  /** orgs the signed-in person can authorize for; the screen asks which when there are several */
  organizations: OAuthConsentOrg[];
  /** an earlier authorization by this user in that org already covers the request */
  previouslyAuthorized: string[];
}

/**
 * Error from the authorize preview. When `redirectTo` is set the client and
 * redirect URI checked out, so the error goes back to the app; otherwise the
 * screen must show it and must NOT redirect (unknown client / unregistered URI).
 */
export interface OAuthAuthorizeError {
  error: string;
  error_description: string;
  redirectTo?: string;
}

export interface OAuthAuthorizeResult {
  redirectTo: string;
}

/** a row on Settings > Apps > Connected apps */
export interface ConnectedAppGrant {
  id: string;
  app: { id: string; name: string; logoUrl: string | null; clientId: string; publisher: string };
  user: { id: string; name: string; email: string };
  scopes: string[];
  authorizedAt: string;
  lastUsedAt: string | null;
}

/** splits an RFC 6749 scope string ("a b c") into distinct names */
export function parseScopeParam(scope: string | undefined | null): string[] {
  return [...new Set((scope ?? "").split(/\s+/).filter(Boolean))];
}
